/**
 * The swipeable survey row (Task 3) — what replaces "Save a backup instead"
 * under the unlinked card and "Back up or move surveys" at the foot of the
 * list. Her words: *"Swiping left lets the user delete or back up a
 * survey."* The device screen and its route are unchanged (Decision #4) —
 * this is a second door onto the two things it already does, not a move of
 * either.
 *
 * PRESENTATIONAL in shape, CONNECTED in fact, like `RemoveSurvey` and
 * `SurveyTransfer` beside which it stands: it reads the store to remove a
 * survey and to save a copy of one. Every decision about WHEN — where the
 * surface sits mid-drag, what counts as open, which way a fling resolves,
 * which actions the rail shows, whether a tap asks first — is
 * `src/ui/state/swipeRow.ts`; this file is React pointer events and GSAP,
 * the same split `PhotoFan.tsx` uses over `photoFan.ts`.
 *
 * ── Reused, not reimplemented ────────────────────────────────────────────────
 * Delete is `removalView` + `useRemoveSurvey()`, exactly as `RemoveSurvey`
 * does it, including `ConfirmPanel` and the per-path honesty of its copy — a
 * blocked survey (submitted and sent) shows NO delete action, never a
 * disabled button. Back up is `saveSurveyBackup` (src/ui/state/download.ts),
 * the same function `SaveSurveyCopy` calls on the building screen — but NOT
 * its `outcome.message` any more (Round 5, her words, item 6: *"there's text
 * that says 'Saved … Keep it somewhere you can find it', I don't want this
 * text displayed"*). This row backs up and closes, silently; `SaveSurveyCopy`
 * is unchanged and still prints it.
 *
 * ── The gesture ──────────────────────────────────────────────────────────────
 * The row (`.survey-row`, unrenamed — a pwa.spec.ts probe finds text through
 * it) is the surface; a rail of one or two buttons sits behind it, revealed
 * as the surface slides left under a pointer drag. `touch-action: pan-y` on
 * the surface leaves vertical scrolling to the browser and horizontal moves
 * to this component, the same contract the shelf's stage uses. Opening one
 * row closes any other open row — a small module-scope registry, not
 * something `swipeRow.ts`'s pure arithmetic needs to know about.
 */

import gsap from 'gsap';
import { useEffect, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';

import { ConfirmPanel } from './ConfirmPanel';
import { StatusGlyph } from './StatusGlyph';
import type { StoredSurvey } from '../../db/store';
import { hrefFor } from '../routing';
import { saveSurveyBackup } from '../state/download';
import { removalView } from '../state/removal';
import { useServices } from '../state/services';
import type { SurveySummary } from '../state/surveyList';
import { clampOffset, railActions, railTap, resolveSwipe, settleOffset, type SwipeState } from '../state/swipeRow';
import { useRemoveSurvey } from '../state/useSurveys';

export interface SwipeableRowProps {
  record: StoredSurvey;
  summary: SurveySummary;
}

/** Before the DOM can measure the real rail (matches --swipe-action-w, theme.css). */
const FALLBACK_ACTION_W = 92;
/** A drag under this many px in either axis has not declared itself yet. */
const AXIS_LOCK_PX = 8;

/* ── only one row open at a time — a small registry, not arithmetic ────────── */
let openId: string | null = null;
const closers = new Map<string, () => void>();

function claimOpen(id: string, close: () => void): void {
  closers.set(id, close);
  if (openId !== null && openId !== id) closers.get(openId)?.();
  openId = id;
}

function releaseOpen(id: string): void {
  closers.delete(id);
  if (openId === id) openId = null;
}

export function SwipeableRow({ record, summary }: SwipeableRowProps): ReactNode {
  const { store } = useServices();
  const remove = useRemoveSurvey();
  const view = removalView(record, summary.title);
  const actions = railActions(view.blockedNote !== null);

  const railRef = useRef<HTMLDivElement | null>(null);
  const surfaceRef = useRef<HTMLAnchorElement | null>(null);
  const stateRef = useRef<SwipeState>('closed');
  const railWidthRef = useRef(FALLBACK_ACTION_W * actions.length);
  const dragRef = useRef<{
    id: number;
    x: number;
    y: number;
    base: number;
    axis: 'x' | 'y' | null;
    lastX: number;
    lastT: number;
    v: number;
  } | null>(null);
  const draggedRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [savingBackup, setSavingBackup] = useState(false);
  const [backupFailure, setBackupFailure] = useState<string | null>(null);

  const rowId = summary.surveyId;

  // The rail's real width, measured like the hand measures its stage — the
  // fallback above is only what the math uses before this has run once.
  useEffect(() => {
    const node = railRef.current;
    if (!node) return undefined;
    const measure = (): void => {
      const width = node.offsetWidth;
      if (width > 0) railWidthRef.current = width;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [actions.length]);

  useEffect(() => () => releaseOpen(rowId), [rowId]);

  /**
   * iOS pauses requestAnimationFrame — GSAP's ticker — while the share sheet
   * or the Files picker from `runBackup`'s `saveSurveyBackup` is up top of
   * this row; coming back to a visible tab re-asserts the surface's own
   * resting `x` directly (`gsap.set`, a snap) rather than trust that a tween
   * begun before the pause is still going to finish it.
   */
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      const surface = surfaceRef.current;
      if (surface) gsap.set(surface, { x: settleOffset(stateRef.current, railWidthRef.current) });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const settle = (next: SwipeState, animate: boolean): void => {
    const surface = surfaceRef.current;
    const target = settleOffset(next, railWidthRef.current);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (surface) {
      if (!animate || reduced) {
        gsap.set(surface, { x: target });
      } else {
        gsap.to(surface, { x: target, duration: 0.45, ease: 'elastic.out(1, 0.75)', overwrite: 'auto' });
      }
    }
    stateRef.current = next;
    setOpen(next === 'open');
    if (next === 'open') claimOpen(rowId, () => settle('closed', true));
    else releaseOpen(rowId);
  };

  /**
   * Cancelling Delete (or a failed removal) leaves `asking` — which swaps the
   * whole row out for `ConfirmPanel` — and returns to a row that was 'open'
   * when the panel took over. `settle()` cannot run that transition: while
   * `asking` is true `.swipe-row` is unmounted, so `surfaceRef.current` is
   * null and there is no surface to animate. But there is nothing TO animate
   * either — the `<a>` that remounts once `asking` clears is a fresh DOM node
   * with no transform, i.e. already sitting at the closed position — so this
   * only has to bring the bookkeeping (`stateRef`, `open`, the open-row
   * registry) back in line with what is about to be on screen. Left stale,
   * `stateRef.current === 'open'` swallows the row's first tap as a
   * close-in-place (`onClickCapture`) instead of a navigation, and
   * `aria-hidden={!open}` keeps exposing the rail's buttons to the
   * accessibility tree and the tab order on a row that looks closed.
   */
  const resetToClosed = (): void => {
    stateRef.current = 'closed';
    setOpen(false);
    releaseOpen(rowId);
  };

  const onPointerDown = (event: PointerEvent<HTMLAnchorElement>): void => {
    if (!event.isPrimary) return;
    draggedRef.current = false;
    dragRef.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      base: settleOffset(stateRef.current, railWidthRef.current),
      axis: null,
      lastX: event.clientX,
      lastT: performance.now(),
      v: 0,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLAnchorElement>): void => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (drag.axis === null) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (drag.axis === 'x') {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // Some browsers refuse capture on an anchor; the drag still tracks
          // fine from clientX alone.
        }
      }
    }
    if (drag.axis !== 'x') return; // a vertical drag is the browser's to scroll (touch-action: pan-y)
    draggedRef.current = true;
    const now = performance.now();
    const dt = now - drag.lastT;
    if (dt > 0) drag.v = ((event.clientX - drag.lastX) / dt) * 1000;
    drag.lastX = event.clientX;
    drag.lastT = now;
    const clamped = clampOffset(drag.base + dx, railWidthRef.current);
    const surface = surfaceRef.current;
    if (surface) gsap.set(surface, { x: clamped });
  };

  const endDrag = (event: PointerEvent<HTMLAnchorElement>): void => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.id !== event.pointerId) return;
    if (drag.axis !== 'x') return;
    const dx = event.clientX - drag.x;
    const offset = clampOffset(drag.base + dx, railWidthRef.current);
    settle(resolveSwipe(offset, drag.v, railWidthRef.current), true);
    // The swipe's own trailing click, if the browser sends one, arrives in
    // this same task and is swallowed by onClickCapture below.
    window.setTimeout(() => {
      draggedRef.current = false;
    }, 0);
  };

  /** A drag's trailing click opens nothing; a tap while OPEN closes the row instead of navigating. */
  const onClickCapture = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (draggedRef.current) {
      draggedRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (stateRef.current === 'open') {
      event.preventDefault();
      event.stopPropagation();
      settle('closed', true);
    }
  };

  const runBackup = (): void => {
    setSavingBackup(true);
    setBackupFailure(null);
    // `outcome.message` is not read here — she does not want the sentence
    // (see the header). `download.ts` is unchanged; `SaveSurveyCopy` on the
    // Building screen still calls `saveSurveyBackup` and still prints it.
    void saveSurveyBackup(store, [record.surveyId])
      .catch((cause: unknown) => {
        setBackupFailure(cause instanceof Error ? cause.message : 'That did not go through.');
      })
      .finally(() => {
        setSavingBackup(false);
        // A snap, not a tween: the share sheet and the Files picker pause
        // iOS's requestAnimationFrame while they're up, and a `gsap.to` here
        // rides that same ticker — it can still be "in flight" (and the rail
        // still open) whenever this `.finally` actually runs. `gsap.set`
        // renders synchronously, so the rail is genuinely closed the instant
        // this settles.
        settle('closed', false);
      });
  };

  const confirmRemove = (): void => {
    setBusy(true);
    setFailure(null);
    void remove(record.surveyId, view.path).catch((cause: unknown) => {
      // The write did not land — the survey is still here. On success there is
      // nothing further to do: the store's own subscription drops it from
      // Home's list and this row simply stops being rendered.
      setFailure(cause instanceof Error ? cause.message : 'That did not go through.');
      setBusy(false);
      setAsking(false);
      resetToClosed();
    });
  };

  if (asking) {
    return (
      <ConfirmPanel
        title={view.confirmTitle}
        body={view.confirmBody}
        confirmLabel={view.confirmLabel}
        cancelLabel={view.cancelLabel}
        busy={busy}
        onConfirm={confirmRemove}
        onCancel={() => {
          setAsking(false);
          resetToClosed();
        }}
      />
    );
  }

  /*
   * Round 7 task 10, controller ruling 13: every row opens Building now, sealed
   * or not. A submitted survey lands on the same screen `useSurvey().readOnly`
   * disables end to end (SurveyProvider.tsx) — Stephanie's words: *"When the
   * user is on the home screen and clicks on one of their submitted surveys,
   * it should open the Building Config screen in read-only mode."* The sealed
   * face this used to open is still there, off the Survey Map's own "Review &
   * Submit" row (CloseScreen.tsx) — never off Home.
   */
  const target = hrefFor({ name: 'building', surveyId: summary.surveyId });

  return (
    <div className="swipe-row">
      <div className="swipe-row__rail" ref={railRef} aria-hidden={!open}>
        {actions.map((action) =>
          action === 'backup' ? (
            <button
              key="backup"
              type="button"
              className="swipe-row__action swipe-row__action--backup"
              aria-label={`Back up — ${summary.title}`}
              tabIndex={open ? 0 : -1}
              disabled={savingBackup}
              onClick={() => {
                if (railTap('backup') === 'run') runBackup();
              }}
            >
              {savingBackup ? 'Saving…' : 'Back up'}
            </button>
          ) : (
            <button
              key="delete"
              type="button"
              className="swipe-row__action swipe-row__action--delete"
              aria-label={`${view.actionLabel} — ${summary.title}`}
              tabIndex={open ? 0 : -1}
              onClick={() => {
                if (railTap('delete') === 'ask') setAsking(true);
              }}
            >
              {view.actionLabel}
            </button>
          ),
        )}
      </div>
      <a
        className="survey-row"
        href={target}
        ref={surfaceRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
      >
        <span className="survey-row__info">
          <span className="survey-row__name">{summary.title}</span>
          {/* A submitted row that has not actually left the phone says so
            * (D-052 §2): "3 items still sending", counting down; "1 item needs
            * you" when something is parked. Gone the moment the survey is in. */}
          {summary.sendingLine ? (
            <span className="survey-row__sub" data-testid="sending-line">
              {summary.sendingLine}
            </span>
          ) : null}
        </span>
        <StatusGlyph kind={summary.tagKind} label={summary.tagLabel} />
      </a>
      <div aria-live="polite">
        {backupFailure ? (
          <p className="ruleline" data-legibility="helper">
            {backupFailure}
          </p>
        ) : null}
        {failure ? (
          <p className="ruleline" data-legibility="helper">
            {failure}
          </p>
        ) : null}
      </div>
    </div>
  );
}
